import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  // ----------------- public ------------------
  {
    path: '/',
    component: () => import('@/views/PublicLayout.vue'),
    children: [
      { path: '',             name: 'home',        component: () => import('@/views/HomeView.vue') },
      { path: 'shop',         name: 'shop',        component: () => import('@/views/ShopView.vue') },
      { path: 'games',        name: 'games',       component: () => import('@/views/GamesView.vue') },
      { path: 'faq',          name: 'faq',         component: () => import('@/views/FAQView.vue') },
      { path: 'checkout',     name: 'checkout',    component: () => import('@/views/CheckoutView.vue') },
      { path: 'impressum',    name: 'impressum',   component: () => import('@/views/ImpressumView.vue') },
      { path: 'datenschutz',  name: 'datenschutz', component: () => import('@/views/DatenschutzView.vue') },
      { path: 'login',        name: 'login',       component: () => import('@/views/LoginView.vue') }
    ]
  },

  // ----------------- authentifiziert ------------------
  {
    path: '/me',
    component: () => import('@/views/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '',         name: 'profile',  component: () => import('@/views/ProfileView.vue') },
      { path: 'sessions', name: 'sessions', component: () => import('@/views/SessionsView.vue') }
    ]
  },

  // ----------------- admin ------------------
  {
    path: '/admin',
    component: () => import('@/views/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '',                name: 'admin.dashboard', component: () => import('@/views/AdminDashboardView.vue') },
      { path: 'players',         name: 'admin.players',   component: () => import('@/views/AdminPlayersView.vue') },
      { path: 'players/:uuid',   name: 'admin.player',    component: () => import('@/views/AdminPlayerDetailView.vue'), props: true },
      { path: 'bans',            name: 'admin.bans',      component: () => import('@/views/AdminBansView.vue') },
      { path: 'crates',          name: 'admin.crates',    component: () => import('@/views/AdminCratesView.vue') },
      { path: 'broadcast',       name: 'admin.broadcast', component: () => import('@/views/AdminBroadcastView.vue') },
      { path: 'audit',           name: 'admin.audit',     component: () => import('@/views/AdminAuditView.vue') }
    ]
  },

  // 404
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() { return { top: 0 }; }
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // Restore-Versuch nur einmal, beim ersten Navigieren — App.vue triggert's
  // bereits onMounted, aber bei Hardrefresh auf /me etc. kommt der Guard hier
  // moeglicherweise vor App's mount-Hook an.
  if (!auth.attempted) await auth.restore();

  if (to.meta.requiresAuth && !auth.user) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'profile' };
  }
});

// Stale-Chunk-Recovery: wenn ein lazy-import fehlschlaegt (typisch wenn der
// User noch einen alten index.html im Cache hat und der Browser einen
// inzwischen geloeschten JS-Chunk anfordert), forcen wir ein hartes Reload.
// Das holt automatisch die frische index.html und damit die aktuellen
// Chunk-Namen. Wir nehmen sessionStorage als Schutz vor Endlos-Reload-Schleife.
router.onError((err) => {
  const msg = String(err?.message ?? '');
  const isChunkError =
    /Loading chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg);
  if (!isChunkError) return;

  const RELOAD_KEY = 'shruby_chunk_reload_ts';
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  if (Date.now() - last < 10_000) {
    // Schon einmal kuerzlich reloadet — vermutlich ist wirklich was kaputt.
    // Hier nicht weiter reloaden, sonst Schleife.
    return;
  }
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

export default router;
